import { notifications } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import useImmediateAction from 'api/use-immediate-action';
import RevisionForm, { RevisionFormData, RevisionDevice } from 'components/forms/revision/RevisionForm';
import { REVISION_REDIRECT_AFTER_SAVE } from 'env';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { FORMS_PAGE_PATH, REVISIONS_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

const DEVICES_EDITABLE_STATUSES = ['created', 'accepted-by-technician', 'declined-by-technician', 'in-progress'];
const TECHNICIAN_EDITABLE_STATUSES = ['created', 'declined-by-technician', 'in-progress'];

/**
 * Page used to edit a revision.
 *
 */
export default function EditRevisionPage() {
  const navigate = useNavigate();
  const { revisionId } = useParams();
  const { getAction } = useApi();

  const { data, loading, error } = useImmediateAction(() => {
    const revisionGetAction = getAction('RevisionGet');
    return revisionGetAction({ parameters: { revisionId: revisionId! } });
  });

  if (error) {
    panic(error);
    return <></>;
  }

  if (!revisionId) {
    return <Navigate to={REVISIONS_PAGE_PATH.original} />;
  }

  /**
   * Handles the form submission.
   */
  function onSubmit({ revisionName, deadline, technicianId, deviceTypeId, devices }: RevisionFormData) {
    const mappedDevices = Object.keys(devices)
      .map((deviceId) => {
        const device = devices[deviceId];
        return {
          deviceId: Number(deviceId),
          deviceName: device.deviceName,
          termId: device.termId && device.termId !== '' ? Number(device.termId) : undefined,
          deviceSubtypeId: device.deviceSubtypeId,
          deviceSubtypeName: device.deviceSubtypeName,
          checked: device.checked,
          manufacturer: device.manufacturer === '' ? undefined : device.manufacturer,
          type: device.type,
          serialNumber: device.serialNumber === '' ? undefined : device.serialNumber,
          manufactured: device.manufactured ?? undefined,
          internalPossessionsNumber: device.internalPossessionsNumber ?? '',
          location: device.location ?? '',
          organization: device.organization ?? { organizationId: 0, organizationName: '' },
        };
      })
      .filter((device) => device.checked);

    const revisionUpdateAction = getAction('RevisionUpdate');

    return revisionUpdateAction({
      parameters: { revisionId: String(revisionId) },
      payload: {
        revisionName,
        deadline: deadline || undefined,
        technicianId: technicianId ? Number(technicianId) : undefined,
        ...(data?.remoteSource?.type === 'chastia' ? { deviceTypeId: Number(deviceTypeId) } : {}),
        devices: mappedDevices,
      },
    })
      .then(() => {
        notifications.show({
          title: data?.kind === 'revision' ? 'Revízna správa bola upravená' : 'Formulár bol upravený',
          message:
            data?.kind === 'revision'
              ? `Revízna správa ${revisionName} bola úspešne upravená.`
              : `Formulár ${revisionName} bol úspešne upravený.`,
          color: SUCCESS_NOTIFICATION_COLOR,
        });

        if (REVISION_REDIRECT_AFTER_SAVE === 'list') {
          navigate(data?.kind === 'revision' ? REVISIONS_PAGE_PATH.original : FORMS_PAGE_PATH.original);
        }
      })
      .catch(panic);
  }

  const status = data?.revisionStatus.slug ?? '';

  return (
    <DashboardLayout
      title={`${data?.kind === 'revision' ? 'Úprava revíznej správy' : 'Úprava formulára'}: ${
        data?.revisionName ?? ''
      }`}
      breadcrumbs={[
        {
          title: data?.kind === 'revision' ? 'Revízne správy' : 'Formuláre',
          link: data?.kind === 'revision' ? REVISIONS_PAGE_PATH.original : FORMS_PAGE_PATH.original,
        },
        { title: data?.revisionName ?? 'Načítavanie ...' },
      ]}
    >
      {!loading && (
        <RevisionForm
          context="edit"
          kind={data!.kind === 'revision' ? 'revision' : 'form'}
          activeDeviceTypesOnly={data.remoteSource?.type !== 'chastia'}
          initialValues={{
            subKind: data.kind,
            revisionName: data.revisionName,
            deadline: data.deadline ?? '',
            organizationId: String(data.organization.organizationId),
            departmentId: String(data.department.departmentId),
            deviceTypeId: String(data.deviceType.deviceTypeId),
            devices: data.devices.reduce((acc, device) => {
              acc[String(device.deviceId)] = {
                deviceId: device.deviceId,
                deviceName: device.deviceName,
                termId: device.termId ? String(device.termId) : '',
                deviceTypeId: device.deviceType?.deviceTypeId ?? 0,
                deviceSubtypeId: device.deviceSubtype?.deviceTypeId ?? 0,
                deviceSubtypeName: device.deviceSubtype?.deviceTypeName ?? '',
                checked: true,
                manufacturer: device.manufacturer,
                type: device.type,
                serialNumber: device.serialNumber,
                manufactured: device.manufactured,
                internalPossessionsNumber: device.internalPossessionsNumber ?? '',
                building: device.building ?? '',
                location: device.location ?? '',
                parentDevice: undefined, // TODO: Extend API to include parent and child devices if needed
                childDevices: undefined, // TODO: Extend API to include parent and child devices if needed
                organization: device.organization,
                structure: device.structure,
                revisionPlan: (device.revisionPlan || [])
                  .filter((rp) => rp.deviceTypeId === data.deviceType.deviceTypeId)
                  .map((rp) => ({
                    revisionPlanId: rp.revisionPlanId!,
                    description: rp.description,
                    date: rp.date,
                    revisionPeriod: rp.revisionPeriod,
                    neverExecuted: rp.neverExecuted,
                    deviceTypeId: rp.deviceTypeId ?? device.deviceType?.deviceTypeId ?? 0,
                  })),
              } satisfies RevisionDevice;

              return acc;
            }, {} as { [deviceId: string]: RevisionDevice }),
            technicianId: data.technician ? String(data.technician.userId) : '',
          }}
          allowEmptyTechnician={status === 'in-progress' ? true : undefined}
          onSubmit={onSubmit}
          readOnly={{
            subKind: true,
            organizationId: true,
            departmentId: true,
            deviceTypeId: data.remoteSource?.type !== 'chastia' || !DEVICES_EDITABLE_STATUSES.includes(status),
            technicianId: !TECHNICIAN_EDITABLE_STATUSES.includes(status),
            devices: data.remoteSource?.type === 'chastia' || !DEVICES_EDITABLE_STATUSES.includes(status),
          }}
        />
      )}
    </DashboardLayout>
  );
}
